import { useState } from "react";
import { MARCADORES_POR_FOLHA, TAMANHO_MAXIMO_DO_ESTILO } from "@foto/shared";
import { trpc } from "../../lib/trpc.js";
import { baixar, buscarFolhaDeMarcadores } from "../../lib/api.js";
import { Area, Botao, Falha, Girando, Selo } from "../../components/ui.js";
import { VisorDeImagem } from "../../components/VisorDeImagem.js";
import { AREA_UTIL_DA_FOLHA, TAMANHO_DA_FOLHA } from "../../components/visor.js";
import { useAuth } from "../auth/useAuth.js";
import { ROTA_DAS_IMPRESSORAS } from "../impressao/alvo.js";
import { compartilharArquivo, podeCompartilharArquivo } from "../impressao/entrega.js";
import { algumaPodeServir } from "../impressao/impressora.js";
import { PainelImprimir } from "../impressao/PainelImprimir.js";
import { perdeONomeDoEstilo } from "./estilos.js";

/**
 * Um conjunto de marcadores já feito: a folha pronta para imprimir e, abaixo,
 * uma linha por marcador com o que a conferência achou e o "Refazer este".
 *
 * A folha é o arquivo final — 1181 × 1748 px, com guias de corte pontilhadas —;
 * os números sobre ela só dizem qual linha cuida de qual faixa.
 *
 * Há dois caminhos até o papel, como na tela de Impressão: "Imprimir" manda a folha à impressora pelo
 * servidor (só alcança a da rede da casa), e "Baixar"/"Compartilhar" levam o JPEG ao app da Canon, que
 * sempre funciona. O principal é o primeiro quando alguma impressora pode ser alcançada.
 */
export function FolhaDeMarcadores({
  id,
  restantes,
  aoApagar,
}: {
  id: number;
  /** Fotos que o pacote ainda comporta: nulo = sem limite; indefinido = carregando. */
  restantes: number | null | undefined;
  aoApagar: () => void;
}) {
  const utils = trpc.useUtils();
  const conjunto = trpc.marcadores.get.useQuery({ id });
  const [refazendo, setRefazendo] = useState<number | null>(null);
  const [direcao, setDirecao] = useState("");
  const [apagando, setApagando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<"compartilhar" | null>(null);
  const [podeCompartilhar] = useState(podeCompartilharArquivo);

  // Impressão direta. A lista só decide qual botão é o principal; o painel faz a própria consulta, nova,
  // ao abrir. Sem resposta (carregando ou falhou), o principal continua sendo o JPEG para o app da Canon.
  const { ehAdmin } = useAuth();
  const [imprimindo, setImprimindo] = useState(false);
  // O visor da folha em tela cheia, para conferir um acento ou o que está perto do picotado.
  const [ampliando, setAmpliando] = useState(false);
  const impressoras = trpc.impressao.impressoras.useQuery();
  const impressaoDireta = impressoras.data ? algumaPodeServir(impressoras.data) : false;

  const atualizar = async () => {
    await Promise.all([
      utils.marcadores.get.invalidate({ id }),
      utils.marcadores.list.invalidate(),
      utils.lotes.saldo.invalidate(),
    ]);
  };
  const refazer = trpc.marcadores.refazer.useMutation({
    onSuccess: async () => {
      setRefazendo(null);
      setErro(null);
      await atualizar();
    },
    onError: (e) => setErro(e.message),
  });
  const apagar = trpc.marcadores.delete.useMutation({
    onSuccess: async () => {
      await utils.marcadores.list.invalidate();
      aoApagar();
    },
    onError: (e) => setErro(e.message),
  });

  if (conjunto.isLoading) return <Girando className="mx-auto my-12 size-6 text-texto-fraco" />;
  if (conjunto.isError) {
    return (
      <Falha mensagem={conjunto.error.message} tentarNovamente={() => void conjunto.refetch()} />
    );
  }
  const c = conjunto.data!;
  const semSaldo = restantes === 0;
  const nome = `marcadores-${c.id}.jpg`;

  async function compartilhar() {
    setOcupado("compartilhar");
    setErro(null);
    try {
      await compartilharArquivo(await buscarFolhaDeMarcadores(id), nome, "Marcadores de livro");
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível compartilhar");
    }
    setOcupado(null);
  }

  return (
    <div className="flex flex-col gap-4">
      {/* A folha é um botão: tocar nela abre o visor. O "Ampliar" no canto é o que a denuncia — sem ele,
          uma imagem parada não diz que dá para tocar. Tudo dentro do botão é `span`, que é o que o HTML
          aceita ali; os números 1–3 seguem só ligando as faixas às linhas abaixo, sem pegar o clique. */}
      <button
        type="button"
        aria-label="Abrir a folha em tela cheia para ampliar"
        onClick={() => setAmpliando(true)}
        className="group relative mx-auto block w-full max-w-[26rem] cursor-zoom-in rounded-peca"
      >
        <img
          src={c.folha.tela}
          alt={`Folha com os três marcadores de ${c.campos.titulo}`}
          className="block aspect-[1181/1748] w-full rounded-peca bg-white"
        />
        <span aria-hidden className="pointer-events-none absolute inset-x-0 top-2 flex">
          {Array.from({ length: MARCADORES_POR_FOLHA }, (_, i) => (
            <span key={i} className="flex flex-1 justify-center">
              <span className="grid size-6 place-items-center rounded-full bg-fundo/80 text-[0.75rem] font-medium text-texto">
                {i + 1}
              </span>
            </span>
          ))}
        </span>
        <span
          aria-hidden
          className="pointer-events-none absolute right-2 bottom-2 rounded-full bg-fundo/80 px-2.5 py-1 text-[0.75rem] font-medium text-texto transition-colors group-hover:bg-fundo"
        >
          Ampliar
        </span>
      </button>

      <ul className="flex flex-col divide-y divide-borda rounded-peca border border-borda bg-superficie">
        {Array.from({ length: MARCADORES_POR_FOLHA }, (_, i) => {
          const faltou = c.faltam.includes(i);
          const aviso = c.avisos[i];
          // Nulo: a direção foi escrita à mão, veio de um modelo, ou o conjunto é de antes dos estilos.
          const estilo = c.estilosNomes[i] ?? null;
          return (
            <li key={i} className="flex flex-col gap-2 px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-[0.9375rem] font-medium text-texto">Marcador {i + 1}</span>
                  {faltou ? (
                    <Selo tom="descartar">Faltou</Selo>
                  ) : aviso ? (
                    <Selo tom="brasa">Conferir</Selo>
                  ) : (
                    <Selo tom="quieto">Texto conferido</Selo>
                  )}
                </div>
                <Botao
                  tom="quieto"
                  className="min-h-11"
                  disabled={refazendo !== null || refazer.isPending || semSaldo}
                  onClick={() => {
                    setErro(null);
                    setDirecao(c.estilos[i] ?? "");
                    setRefazendo(i);
                  }}
                  aria-label={`Refazer o marcador ${i + 1}`}
                >
                  Refazer este
                </Botao>
              </div>
              {/* Numa linha à parte: o nome do estilo é longo, e ao lado do estado empurraria o
                  botão para baixo só nas faixas que o têm. */}
              {estilo && (
                <div>
                  <Selo tom="projecao">
                    <span className="sr-only">Estilo: </span>
                    {estilo}
                  </Selo>
                </div>
              )}
              {aviso && (
                <p className="text-[0.8125rem] leading-relaxed text-texto-suave">{aviso}</p>
              )}

              {refazendo === i && (
                <div className="flex flex-col gap-3 rounded-peca border border-borda bg-fundo p-3">
                  <label className="block">
                    <span className="mb-1.5 block text-[0.8125rem] font-medium text-texto-suave">
                      Direção de arte deste marcador
                    </span>
                    <Area
                      value={direcao}
                      onChange={(e) => setDirecao(e.target.value)}
                      maxLength={TAMANHO_MAXIMO_DO_ESTILO}
                      className="min-h-[5rem]"
                    />
                    {perdeONomeDoEstilo(estilo, c.estilos[i] ?? "", direcao) && (
                      <span className="mt-1.5 block text-[0.8125rem] text-texto-fraco">
                        Com o texto alterado, este marcador deixa de ser “{estilo}”.
                      </span>
                    )}
                  </label>
                  <p className="text-[0.8125rem] text-texto-suave">
                    Consome 1 foto do pacote
                    {typeof restantes === "number" ? ` (restam ${restantes})` : ""}, só se o novo
                    marcador sair.
                  </p>
                  {/* O andamento ("Refazendo o marcador 2…") está na faixa presa no topo da página. */}
                  <div className="flex flex-wrap gap-2">
                    <Botao
                      tom="brasa"
                      carregando={refazer.isPending}
                      disabled={direcao.trim().length < 10}
                      onClick={() =>
                        refazer.mutate({
                          id,
                          indice: i,
                          // Só manda se mudou: a direção do modelo continua valendo.
                          ...(direcao.trim() !== (c.estilos[i] ?? "") && { estilo: direcao }),
                        })
                      }
                    >
                      Refazer
                    </Botao>
                    <Botao
                      tom="quieto"
                      disabled={refazer.isPending}
                      onClick={() => setRefazendo(null)}
                    >
                      Cancelar
                    </Botao>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {semSaldo && (
        <p className="text-[0.8125rem] text-texto-suave">
          O pacote não tem mais fotos para refazer um marcador.
        </p>
      )}

      {c.pose === "ajustar" && (
        <p className="text-[0.8125rem] leading-relaxed text-texto-suave">
          Neste conjunto a IA ajustou a pose das pessoas, e “Refazer este” repete o ajuste. Confira
          os rostos: mudar a pose aumenta o risco de a IA se afastar da foto.
        </p>
      )}

      <p className="text-[0.8125rem] leading-relaxed text-texto-fraco">
        As três faixas ficam dentro da área que o papel imprime e mantém (93,6 × 141 mm), com margem
        branca em volta: o picotado do postal come as bordas. Corte nas linhas pontilhadas.
      </p>

      <p className="text-[0.8125rem] leading-relaxed text-texto-fraco">
        A IA redesenha as pessoas da foto: confira se estão reconhecíveis antes de imprimir. O texto
        pequeno da capa do livro sai inventado e ilegível no tamanho impresso.
      </p>

      {erro && <Falha mensagem={erro} />}

      <div className="flex flex-wrap gap-2 border-t border-borda pt-4">
        <Botao
          tom={impressaoDireta ? "brasa" : "quieto"}
          className="min-h-11"
          // Com um conjunto sendo refeito a folha vai mudar: imprimir agora mandaria a antiga.
          disabled={refazer.isPending}
          onClick={() => setImprimindo(true)}
        >
          Imprimir
        </Botao>
        <Botao
          tom={impressaoDireta ? "quieto" : "brasa"}
          className="min-h-11"
          onClick={() => baixar(`/api/marcadores/${c.id}/folha`)}
        >
          Baixar JPEG
        </Botao>
        {podeCompartilhar && (
          <Botao
            tom="quieto"
            className="min-h-11"
            carregando={ocupado === "compartilhar"}
            onClick={() => void compartilhar()}
          >
            Compartilhar com o app
          </Botao>
        )}
        {apagando ? (
          <>
            <span className="self-center text-[0.8125rem] text-texto-suave">
              Apagar este conjunto?
            </span>
            <Botao
              tom="descartar"
              className="min-h-11"
              carregando={apagar.isPending}
              onClick={() => apagar.mutate({ id })}
            >
              Apagar
            </Botao>
            <Botao tom="quieto" className="min-h-11" onClick={() => setApagando(false)}>
              Não apagar
            </Botao>
          </>
        ) : (
          <Botao tom="fantasma" formato="nu" className="min-h-11" onClick={() => setApagando(true)}>
            Apagar
          </Botao>
        )}
      </div>

      {ampliando && (
        <VisorDeImagem
          titulo={`Folha de marcadores · ${c.campos.titulo}`}
          fotoUrl={`/api/marcadores/${c.id}/folha`}
          previa={c.folha.tela}
          tamanho={TAMANHO_DA_FOLHA}
          alt={`Folha com os três marcadores de ${c.campos.titulo}`}
          areaUtil={AREA_UTIL_DA_FOLHA}
          aoFechar={() => setAmpliando(false)}
        />
      )}

      {imprimindo && (
        <PainelImprimir
          alvo={{
            tipo: "marcadores",
            conjuntoId: c.id,
            faixas: MARCADORES_POR_FOLHA,
            faixasSemArte: c.faltam.length,
          }}
          ehAdmin={ehAdmin}
          podeCompartilhar={podeCompartilhar}
          aoFechar={() => setImprimindo(false)}
          aoBaixar={() => {
            setImprimindo(false);
            baixar(`/api/marcadores/${c.id}/folha`);
          }}
          aoCompartilhar={() => {
            setImprimindo(false);
            void compartilhar();
          }}
          // O cadastro de impressoras mora na tela de Impressão: aqui só se aponta o caminho.
          cadastro={{ tipo: "pagina", para: ROTA_DAS_IMPRESSORAS }}
        />
      )}
    </div>
  );
}
